import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode, useState} from 'react';

import {ZINDEX_TAB_LAYOUT_HEADER} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_LIGHT, GRAY_LIGHTER, WHITE} from 'ui-library/Colors';
import {UIEvent} from 'util/Types';

export enum TabLayoutBackgroundColor {
  WHITE = 'WHITE',
  GRAY_LIGHTER = 'GRAY_LIGHTER',
}

const BODY_BACKGROUND_COLOR_TO_HEX: {[key in TabLayoutBackgroundColor]: string} = {
  [TabLayoutBackgroundColor.WHITE]: WHITE,
  [TabLayoutBackgroundColor.GRAY_LIGHTER]: GRAY_LIGHTER,
};

export interface TabLayoutProps {
  /** Background color applied to the TabLayout's body. Defaults to WHITE. */
  backgroundColor: TabLayoutBackgroundColor;
  children: ReactNode;
  className?: string;
  /** Main content of the layout; rendered under the header */
  dataTestAttrs?: DataTestAttrs;
  header: ReactNode;
  /** If true, the header will be frozen and a box shadow will appear when scrolling the layout's body.
   *
   * @default true
   */
  isHeaderFrozen?: boolean;
}

/**
 * Render a layout with a header and body.
 *
 *
 * Padding is applied to the header, but not to the body.
 *
 * ```markdown
 * {header}
 * --------
 * {children}
 * ```
 *
 * @example
 * <TabLayout header="Title">Content</TabLayout>
 *
 * Title
 * -----
 * Content
 *
 * @storybook https://dev--622a4c57262ed1003a5a57fa.chromatic.com/?path=/story/ui-library-tablayout--basic
 */
export default function TabLayout({
  backgroundColor,
  children,
  className,
  dataTestAttrs,
  header,
  isHeaderFrozen = true,
}: TabLayoutProps): JSX.Element {
  const [distanceFromTop, setDistanceFromTop] = useState(0);

  function handleScroll(e: UIEvent): void {
    setDistanceFromTop(Math.min(e.currentTarget.scrollTop, 5));
  }

  return (
    <div
      className={classNames(
        css(styles.wrapper),
        !isHeaderFrozen && css(styles.scrollableWrapper),
        'scrollbar-override',
        className
      )}
      {...getTestProps(dataTestAttrs ?? {component: 'TabLayout', element: 'root'})}
    >
      <div
        className={css(styles.header, isHeaderFrozen && styles.frozenHeader)}
        style={
          isHeaderFrozen
            ? {
                boxShadow: `0 ${distanceFromTop}px ${distanceFromTop}px 0 rgba(200, 200, 200, 0.5)`,
              }
            : {}
        }
        {...getTestProps({component: 'TabLayout', element: 'header'})}
      >
        {header}
      </div>
      <div
        onScroll={isHeaderFrozen ? handleScroll : undefined}
        className={classNames(
          css(styles.body, isHeaderFrozen && styles.scrollableBody),
          'scrollbar-override'
        )}
        style={{backgroundColor: backgroundColor && BODY_BACKGROUND_COLOR_TO_HEX[backgroundColor]}}
        {...getTestProps({component: 'TabLayout', element: 'body'})}
      >
        {children}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    // Prevent box shadow from leaking out of edges of the layout
    overflowX: 'hidden',
  },
  scrollableWrapper: {
    overflowY: 'auto',
  },
  header: {
    padding: '12px 12px 12px 14px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  frozenHeader: {
    zIndex: ZINDEX_TAB_LAYOUT_HEADER,
  },
  body: {
    flex: 1,
  },
  scrollableBody: {
    overflowY: 'auto',
    flex: 1,
  },
});
